// =============================================================================
// MobileLayout.tsx
// Top-level mobile shell — header, tab content, bottom nav. Owns all the
// shared state (filters, sheet visibility, order data, inventory data) and
// hands it down to the per-tab components and the bottom sheet stack.
// =============================================================================

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { getOrders, getInventory } from '../../lib/api';

import { Order, InventoryData, ConfigData, OrderCounts } from '../../lib/types';
import type { Vendor } from '../../lib/types';

import { S, MOBILE_CSS }     from './mobileStyles';
import MobileDashboard       from './MobileDashboard';
import MobileInventory       from './MobileInventory';
import MobileAnalytics       from './MobileAnalytics';
import MobileReports         from './MobileReports';
import MobileSheets          from './MobileSheets';
import MobileAddOrder        from './MobileAddOrder';
import MobileInventorySheet  from './MobileInventorySheet';
import { DateRangePicker, DateRange } from './sheets/DateRangePicker';
import { useMobileDateFilters }       from './hooks/useMobileFilters';

// ── TYPES ────────────────────────────────────────────────────────────

interface MobileLayoutProps {
  session: any;
  configData: ConfigData | null;
  inventoryData: InventoryData | null;
  vendors: Vendor[];
  isDark: boolean;
  setIsDark: (v: boolean) => void;
  onSignOut: () => void;
  onOrderAdded: () => void;
  onInventoryChanged: () => void;
  showToast: (msg: string, type?: 'success' | 'error') => void;
}

// ── CONSTANTS ────────────────────────────────────────────────────────

const NAV_TABS = [
  { id: 'dashboard', label: 'Dashboard', icon: <><rect x="3" y="3" width="7" height="7" rx="2" /><rect x="14" y="3" width="7" height="7" rx="2" /><rect x="3" y="14" width="7" height="7" rx="2" /><rect x="14" y="14" width="7" height="7" rx="2" /></> },
  { id: 'inventory', label: 'Inventory', icon: <><rect x="2" y="7" width="20" height="14" rx="3" /><path d="M7 7V5.5C7 4.1 8.1 3 9.5 3h5c1.4 0 2.5 1.1 2.5 2.5V7" /></> },
  { id: 'analytics', label: 'Analytics', icon: <><path d="M3 20V8l4-5h10l4 5v12" /><path d="M7 12h4v4H7z" /></> },
  { id: 'reports',   label: 'Reports',   icon: <><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><path d="M14 2v6h6" /></> },
] as const;

// ── MAIN COMPONENT ───────────────────────────────────────────────────

export default function MobileLayout({
  session, configData, inventoryData, vendors,
  isDark, setIsDark, onSignOut, onOrderAdded, onInventoryChanged, showToast,
}: MobileLayoutProps) {

  // Tab state
  const [activeTab, setActiveTab] = useState<'dashboard' | 'inventory' | 'analytics' | 'reports'>('dashboard');
  const [isCollapsed, setIsCollapsed] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Sheet visibility state
  const [showAvatar, setShowAvatar] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [closingSheet, setClosingSheet] = useState<string | null>(null);

  // Add order state
  const [showAddOrder, setShowAddOrder] = useState(false);
  const [editingOrder, setEditingOrder] = useState<Order | null>(null);

  // Inventory detail state
  const [selectedItem, setSelectedItem] = useState<any>(null);

  // Filter state
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterDept, setFilterDept] = useState('all');
  const [filterCat, setFilterCat] = useState('all');

  // Shared date range — Dashboard list + stat cards filter on this; Analytics
  // forwards from/to to fetchAnalytics. Inventory History owns its own range
  // (different time-axis: log timestamps vs. order created_at).
  const [dateRange, setDateRange] = useState<DateRange>({ value: 'all', from: '', to: '' });
  const [showDateRange, setShowDateRange] = useState(false);
  const [invHistDateRange, setInvHistDateRange] = useState<DateRange>({ value: 'all', from: '', to: '' });
  const [showInvHistDateRange, setShowInvHistDateRange] = useState(false);

  // Inventory state
  const [invView, setInvView] = useState<'items' | 'history'>('items');
  const [invSearch, setInvSearch] = useState('');
  // When set, Inventory > History is filtered to a single item (set by
  // "View all" on the item detail sheet). Null = no item filter.
  const [historyItemId, setHistoryItemId] = useState<string | null>(null);
  const [historyItemName, setHistoryItemName] = useState<string>('');

  const handleViewAllHistory = useCallback((itemId: string, itemName: string) => {
    setHistoryItemId(itemId);
    setHistoryItemName(itemName);
    setInvView('history');
    setActiveTab('inventory');
  }, []);

  // Analytics state
  const [analyticsView, setAnalyticsView] = useState<'activity' | 'spend'>('spend');

  // Reports state
  const [reportType, setReportType] = useState('all');

  // ── Order data (hybrid: load all, filter client-side) ─────────
  const [allMobileOrders, setAllMobileOrders] = useState<Order[]>([]);
  const [counts, setCounts] = useState<OrderCounts>({ pending: 0, ordered: 0, delivered: 0, closed: 0, total: 0 });

  // Refresh full working set from server (called on mount and after add/delete)
  const refreshOrders = useCallback(async () => {
    const result = await getOrders({ limit: 200 });
    if (result.ok) {
      setAllMobileOrders(result.data || []);
      if (result.counts) setCounts(result.counts);
    }
  }, []);

  // Initial load
  useEffect(() => {
    refreshOrders();
  }, [refreshOrders]);

  // Inventory log + order data — date-range derivations live in a small hook
  // so this file stays under the 400-line cap.
  const { dateRangedOrders, rangedCounts, filteredInvLog } = useMobileDateFilters(
    allMobileOrders,
    counts,
    dateRange,
    inventoryData?.log ?? [],
    invHistDateRange,
  );

  // Client-side filtering (instant — no server calls)
  const filteredOrders = useMemo(() => {
    let result = dateRangedOrders;
    if (filterStatus !== 'all') result = result.filter(o => o.status.toLowerCase() === filterStatus);
    if (filterDept !== 'all') result = result.filter(o => o.department_id === filterDept);
    if (filterCat !== 'all') result = result.filter(o => o.category === filterCat);
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(o =>
        (o.order_number || '').toLowerCase().includes(q) ||
        (o.vendor || '').toLowerCase().includes(q) ||
        (o.staff_name || '').toLowerCase().includes(q) ||
        (o.notes || '').toLowerCase().includes(q) ||
        (o.order_items || []).some(i => (i.item_name || '').toLowerCase().includes(q))
      );
    }
    return result;
  }, [dateRangedOrders, filterStatus, filterDept, filterCat, search]);

  // Wrapped onOrderAdded — refresh mobile data too
  const handleMobileOrderAdded = useCallback(() => {
    onOrderAdded();
    refreshOrders();
  }, [onOrderAdded, refreshOrders]);

  // Derived data
  const locale = configData?.config?.locale || 'en-NZ';
  const currency = configData?.config?.currency_symbol || '$';
  const categories = configData?.categories || [];
  const departments = (configData?.departments ?? []).filter(d => d.active !== false);
  const staffDirectory = (configData?.directory ?? []).filter(s => s.active !== false);
  const inventory = inventoryData?.inventory ?? [];
  const invLog = inventoryData?.log ?? [];
  const lowThreshold = configData?.config?.low_stock_threshold || 5;

  // Identity for avatar — always render the button. If profile/session haven't
  // resolved yet (or briefly drop), fall through to a neutral '•' so the avatar
  // never visually disappears from the header.
  const identitySource = configData?.profile?.display_name || session?.user?.email || '';
  const initials = identitySource
    ? identitySource.split(' ').map((w: string) => w[0]).join('').slice(0, 2).toUpperCase()
    : '•';

  // Filtered inventory (client-side — inventory is not paginated)
  const filteredInventory = React.useMemo(() => {
    if (!invSearch.trim()) return inventory;
    const q = invSearch.toLowerCase();
    return inventory.filter(i => i.name?.toLowerCase().includes(q) || i.code?.toLowerCase().includes(q));
  }, [inventory, invSearch]);

  // Scroll collapse — only for dashboard and inventory.
  // The previous "CollapsedHeader" sticky bar (mini stats + quick action buttons)
  // was removed: it duplicated controls already present in each tab's own filter
  // bar and added a second row of header chrome that pushed content down on every
  // scroll. Tabs that need an "always visible" search/filter affordance can make
  // their own filter bar sticky in a future pass.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const handler = () => {
      if (activeTab === 'dashboard' || activeTab === 'inventory') {
        setIsCollapsed(el.scrollTop > 120);
      }
    };
    el.addEventListener('scroll', handler, { passive: true });
    return () => el.removeEventListener('scroll', handler);
  }, [activeTab]);

  // Reset on tab change
  useEffect(() => {
    setIsCollapsed(false);
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [activeTab]);

  // Load inventory when switching to inventory tab
  useEffect(() => {
    if (activeTab === 'inventory') {
      getInventory().then(r => { if (r.ok && r.data && onInventoryChanged) onInventoryChanged(); });
    }
  }, [activeTab]);

  // Close add order and reset editing
  const handleCloseAddOrder = () => {
    setShowAddOrder(false);
    setEditingOrder(null);
  };

  // ═══ RENDER ═══
  const tabLabel = activeTab === 'dashboard' ? 'Dashboard' : activeTab === 'inventory' ? 'Inventory' : activeTab === 'analytics' ? 'Analytics' : 'Reports';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100dvh', background: 'var(--bg)', fontFamily: '-apple-system, system-ui, sans-serif', overflow: 'hidden' }}>

      <style>{MOBILE_CSS}</style>

      {/* Safe area — always visible */}
      <div style={{ ...S.safe, background: 'var(--surface)', flexShrink: 0 }} />

      {/* Header — always visible, ~58px tall. App icon (left) stays small as a
          secondary mark; avatar (right) is the primary identity affordance and
          is sized for both presence and tap comfort. */}
      <div style={S.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 22, height: 22, background: 'var(--accent)', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg viewBox="0 0 96 96" style={{ width: 12, height: 12 }} fill="none"><rect x="24" y="28" width="48" height="56" rx="8" fill="rgba(255,255,255,0.16)" /><rect x="38" y="28" width="20" height="9" style={{ fill: 'var(--accent)' }} /><rect x="38" y="18" width="20" height="19" rx="5" fill="rgba(255,255,255,0.92)" /><path d="M31 69 L61 69" stroke="white" strokeWidth="3.5" strokeLinecap="round" /><path d="M53 63 L61 69 L53 75" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" fill="none" /></svg>
          </div>
          <span style={{ fontSize: 'var(--m-text-body)', fontWeight: 600 }}>{tabLabel}</span>
        </div>
        <div style={{ flex: 1 }} />
        <button onClick={() => setShowAvatar(true)} className="m-btn" style={{ width: 38, height: 38, background: 'var(--accent)', borderRadius: '50%', border: 'none', color: 'white', fontSize: 16, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{initials}</button>
      </div>

      {/* Scrollable content */}
      <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', paddingBottom: 64, WebkitOverflowScrolling: 'touch' }}>
        <div style={{ padding: '10px 12px' }}>

          {activeTab === 'dashboard' && (
            <MobileDashboard
              filteredOrders={filteredOrders} counts={rangedCounts} currency={currency}
              search={search} setSearch={setSearch} filterStatus={filterStatus}
              setFilterStatus={setFilterStatus} setShowFilters={setShowFilters}
              setSelectedOrder={setSelectedOrder} setShowAddOrder={setShowAddOrder}
              isCollapsed={isCollapsed}
              dateRangeActive={dateRange.value !== 'all' && (dateRange.from !== '' || dateRange.to !== '')}
            />
          )}

          {activeTab === 'inventory' && (
            <MobileInventory
              inventory={inventory} filteredInventory={filteredInventory} invLog={filteredInvLog}
              invView={invView} setInvView={setInvView} invSearch={invSearch}
              setInvSearch={setInvSearch} lowThreshold={lowThreshold}
              locale={locale} isCollapsed={isCollapsed}
              setSelectedItem={setSelectedItem}
              historyItemId={historyItemId} historyItemName={historyItemName}
              clearHistoryItem={() => { setHistoryItemId(null); setHistoryItemName(''); }}
              dateRange={invHistDateRange}
              onOpenDateRange={() => setShowInvHistDateRange(true)}
            />
          )}

          {activeTab === 'analytics' && (
            <MobileAnalytics
              currency={currency} analyticsView={analyticsView}
              setAnalyticsView={setAnalyticsView} filterCat={filterCat}
              setFilterCat={setFilterCat} categories={categories}
              dateRange={dateRange}
              onOpenDateRange={() => setShowDateRange(true)}
            />
          )}

          {activeTab === 'reports' && (
            <MobileReports
              orders={filteredOrders} currency={currency} locale={locale}
              departments={departments} staffDirectory={staffDirectory}
              reportType={reportType}
              setReportType={setReportType} setSelectedOrder={setSelectedOrder}
            />
          )}

        </div>
      </div>

      {/* Bottom sheets */}
      <MobileSheets
        session={session} configData={configData} currency={currency} locale={locale}
        departments={departments} categories={categories}
        isDark={isDark} setIsDark={setIsDark} onSignOut={onSignOut}
        onOrderAdded={handleMobileOrderAdded} onInventoryChanged={onInventoryChanged} showToast={showToast}
        selectedOrder={selectedOrder} setSelectedOrder={setSelectedOrder}
        setEditingOrder={setEditingOrder} setShowAddOrder={setShowAddOrder}
        showFilters={showFilters} setShowFilters={setShowFilters}
        filterStatus={filterStatus} setFilterStatus={setFilterStatus}
        filterDept={filterDept} setFilterDept={setFilterDept}
        filterCat={filterCat} setFilterCat={setFilterCat}
        showSearch={showSearch} setShowSearch={setShowSearch}
        search={search} setSearch={setSearch} invSearch={invSearch} setInvSearch={setInvSearch}
        activeTab={activeTab}
        showAvatar={showAvatar} setShowAvatar={setShowAvatar}
        showSettings={showSettings} setShowSettings={setShowSettings}
        closingSheet={closingSheet} setClosingSheet={setClosingSheet}
        dateRange={dateRange}
        onOpenDateRange={() => setShowDateRange(true)}
        onClearDateRange={() => setDateRange({ value: 'all', from: '', to: '' })}
      />

      {/* Date range picker — shared instance for Dashboard + Analytics */}
      <DateRangePicker
        open={showDateRange}
        range={dateRange}
        onApply={setDateRange}
        onClose={() => setShowDateRange(false)}
        closingSheet={closingSheet}
        setClosingSheet={setClosingSheet}
      />

      {/* Date range picker — Inventory History (own state, log timestamps) */}
      <DateRangePicker
        open={showInvHistDateRange}
        range={invHistDateRange}
        onApply={setInvHistDateRange}
        onClose={() => setShowInvHistDateRange(false)}
        closingSheet={closingSheet}
        setClosingSheet={setClosingSheet}
      />

      {/* Inventory item detail sheet */}
      <MobileInventorySheet
        selectedItem={selectedItem} setSelectedItem={setSelectedItem}
        invLog={invLog} staffDirectory={staffDirectory}
        departments={departments} lowThreshold={lowThreshold}
        onInventoryChanged={onInventoryChanged} showToast={showToast}
        closingSheet={closingSheet} setClosingSheet={setClosingSheet}
        onViewAllHistory={handleViewAllHistory}
      />

      {/* Add/Edit order full-screen */}
      <MobileAddOrder
        show={showAddOrder} onClose={handleCloseAddOrder} editingOrder={editingOrder}
        departments={departments} staffDirectory={staffDirectory}
        categories={categories} vendors={vendors}
        onOrderAdded={handleMobileOrderAdded} showToast={showToast}
      />

      {/* Bottom navigation — active tab gets a contained accent-tinted pill behind
          its icon+label. No tab-switch transition (Stage 3). */}
      <div style={S.bottomNav}>
        {NAV_TABS.map(tab => {
          const active = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className="m-nav-btn"
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px', fontFamily: 'inherit' }}
            >
              <div className={`m-nav-pill ${active ? 'is-active' : ''}`}>
                <svg viewBox="0 0 24 24" style={{ width: 20, height: 20, fill: 'none', stroke: active ? 'var(--accent)' : 'var(--text3)', strokeWidth: 1.8 }}>{tab.icon}</svg>
                <span style={{ fontSize: 10, fontWeight: active ? 600 : 400, color: active ? 'var(--accent)' : 'var(--text3)' }}>{tab.label}</span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
